import {DrawFrmThing} from "../../../drawfrm/DrawFrmThing";
import {inches, lineBlueHeavy} from "../../../const";
import {FrmShape} from "../../../drawfrm/FrmShape";
import {FrameProfileArgs} from "./ProfileArgs";
import {FrmRefPoint} from "../../../drawfrm/FrmRefPoint";

// Kerf — frame with a kerf slot cut into the stop to receive weatherstripping.
// Geometry mirrors FrameProfileKerf.svg (drawn at 80px/inch).
export class FrameProfileKerf extends DrawFrmThing {

    primaryDoorAnchor = new FrmRefPoint();
    // top of the outer face; the frame-profile view aligns a clamshell mullion on this.
    faceAnchor = new FrmRefPoint();

    constructor(args: FrameProfileArgs) {
        super();
        const {detail, jambDepth, jambFace, view, app, left, swingIn, clamshell, hideFaces, hideFaceDims, flipY} = args;

        const shape = new FrmShape(`frameProfile:KF:${left ? "left" : "right"}`, {
            dimensionMode: view.dimensionMode,
            thing: this,
            app: app,
        });
        shape.strokeColor = lineBlueHeavy;
        shape.horizontalDimensionSide = left ? "left" : "right";
        if (!left) shape.invertX();
        if((!swingIn) !== !!flipY) shape.invertY();

        //  |--------|              top return + top face
        //  |        |
        //  |        |   throat (flexes with jamb depth)
        //  |        |--|           kerf slot (out, up, out)
        //  |        |  |--|
        //  |           |  | stop face
        //  |        |--|  |
        //  |        |     |
        //  |--------|     |        bottom return + bottom face
        //                          (stop projects toward the door on the right)

        const topReturn = 0.71 * inches;   // stub return into the wall at the top
        const face = 2 * inches;           // jamb face width
        const throatUpper = 2 * inches;    // throat above the kerf (flexes with jamb depth)
        const kerfStep1 = 0.139 * inches;  // first step out toward the kerf
        const kerfRise = 0.391 * inches;   // kerf slot depth (cut back up)
        const kerfStep2 = 0.637 * inches;  // second step out to the stop
        const stopFace = 2.438 * inches;   // height of the door stop face
        const stopBack = 0.776 * inches;   // step back in from the stop
        const throatLower = 1.339 * inches; // throat below the stop (flexes with jamb depth)
        const bottomReturn = 0.511 * inches; // stub return into the wall at the bottom

        // top return (up) then top face (right)
        shape.start(0, 0);
        const topReturnLine = shape.lineTo(0, -topReturn);
        if(clamshell) topReturnLine.setPenUp();
        else topReturnLine.linkDimension(detail.drywallHookDrop, "extended");
        const topFace = shape.lineTo(face, 0).linkDimension(jambFace);

        // upper throat (flexes with jamb depth)
        const flex = shape.lineTo(0, throatUpper - kerfRise);
        flex.linkDimension(detail.rabbetDepth);

        this.primaryDoorAnchor.setLocalPoint(flex.pointB);

        const kerfRiseLine = shape.lineTo(0, kerfRise).linkDimension(detail.kerfDepth);

        // kerf slot: step out, cut back up, step out again to the stop
        shape.lineTo(kerfStep1, 0);
        shape.lineTo(0, -kerfRise).dimMatches(kerfRiseLine);
        shape.lineTo(kerfStep2, 0);

        // door stop face
        shape.lineTo(0, stopFace);

        // step back in from the stop toward the door
        shape.lineTo(-stopBack, 0).fixedLength(stopBack);

        // lower throat (flexes with jamb depth)
        shape.lineTo(0, throatLower)
            .linkDimension(detail.bottomRabbetDepth);

        // bottom face (left) then bottom return (up)
        const bottomFace = shape.lineTo(-face, 0);
        const bottomReturnLine = shape.lineTo(0, -bottomReturn);
        bottomReturnLine.dimMatches(topReturnLine);
        if(clamshell) bottomReturnLine.setPenUp();

        // keep the returns, kerf slot and stop rigid so a jamb-depth edit only
        // flexes the upper/lower throats
        // topReturnLine.fixedLength(topReturn, "top-return");
        // bottomReturnLine.fixedLength(bottomReturn, "bottom-return");
        // kerfStep1Line.fixedLength(kerfStep1, "kerf-step-1");
        // kerfRiseLine.fixedLength(kerfRise, "kerf-rise");
        // kerfStep2Line.fixedLength(kerfStep2, "kerf-step-2");
        // stopFaceLine.fixedLength(stopFace, "stop-face");
        // stopBackLine.fixedLength(stopBack, "stop-back");

        // top and bottom faces stay matched
        bottomFace.dimMatches(topFace);

        // jamb depth drives the overall throat (face-to-face)
        shape.addDimension(topFace.getStartpoint(), bottomFace.getEndpoint(), jambDepth);

        // clamshell secondary side: omit the top/bottom faces so the primary side's
        // faces cap the merged mullion (avoids a doubled/gapped cap).
        if(hideFaces) { topFace.setPenUp(); bottomFace.setPenUp(); }
        if(hideFaces || hideFaceDims) shape.hideDimensionsForVars([jambFace, jambDepth]);
        this.faceAnchor.setLocalPoint(topFace.getStartpoint());
        shape.done();
    }
}
